import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wifi, Eye, Battery, Zap, Star, ChevronRight, ShieldCheck, BadgeCheck, Heart, CheckCircle2, ShoppingBag, MapPin, Package, Sparkles } from 'lucide-react';
import { publicApi } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';
import useSEOPage from '../hooks/useSEOPage';
import { ReviewPhotos } from '../components/PhotoLightbox';
import ProductBadge from '../components/ProductBadge';

function Stars({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14} className={i <= rating ? "text-[#00aaff]" : "text-gray-200"} fill={i <= rating ? "#00aaff" : "#e5e7eb"} />
      ))}
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, tc } = useLanguage();
  
  const { seoData } = useSEOPage('home');
  
  useSEO({
    title: seoData?.meta_title,
    description: seoData?.meta_description,
    ogImage: seoData?.og_image,
    url: 'https://joeycyan.com/'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, reviewsRes] = await Promise.all([
          publicApi.get('/products'),
          publicApi.get('/reviews')
        ]);
        // Deduplicate color variants (keep only the first variant per base product).
        // API returns entries like "<id>__color__noir" when display_by_color is true.
        const seenBaseIds = new Set();
        const deduped = [];
        for (const p of productsRes.data) {
          const baseId = typeof p.id === 'string' ? p.id.split('__color__')[0] : p.id;
          if (seenBaseIds.has(baseId)) continue;
          seenBaseIds.add(baseId);
          deduped.push(p);
        }
        // Sort by homepage_order (ascending, null goes last).
        deduped.sort((a, b) => {
          const ao = a.homepage_order;
          const bo = b.homepage_order;
          if (ao == null && bo == null) return 0;
          if (ao == null) return 1;
          if (bo == null) return -1;
          return ao - bo;
        });
        setProducts(deduped.slice(0, 4));
        setReviews(reviewsRes.data.slice(0, 6));
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Wifi, title: t('home.feat1Title'), desc: t('home.feat1Desc') },
    { icon: Eye, title: t('home.feat2Title'), desc: t('home.feat2Desc') },
    { icon: Battery, title: t('home.feat3Title'), desc: t('home.feat3Desc') },
    { icon: Zap, title: t('home.feat4Title'), desc: t('home.feat4Desc') },
  ];

  return (
    <div className="bg-white text-gray-900">
      {/* Banner */}
      <div className="bg-[#00aaff] py-2.5 px-6 text-center">
        <p className="text-sm text-white font-medium">
          🔗 <span className="font-bold">{t('banner.connected')}</span> {t('banner.details')}
        </p>
      </div>

      {/* Counterfeit warning banner */}
      <div className="bg-amber-50 border-b border-amber-200 py-2.5 px-6" data-testid="counterfeit-warning-banner">
        <p className="text-sm text-amber-900 text-center max-w-6xl mx-auto">
          <span className="inline-block mr-1.5" aria-hidden="true">⚠️</span>
          <span className="font-bold">{t('banner.counterfeitTitle')}</span>
          <span className="ml-1">{t('banner.counterfeitText')}</span>
        </p>
      </div>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black" data-testid="hero-section">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-50" src="/api/uploads/homepage/hero-video.mp4" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00aaff] animate-pulse" />
              <span className="text-xs text-white/90 font-semibold">{t('home.leaderBadge')}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-[0.95] tracking-tight mb-6 text-white">
              {t('home.heroTitle1')}<br />
              <span className="text-[#00aaff]">{t('home.heroTitle2')}</span><br />
              {t('home.heroTitle3')}
            </h1>
            <p className="text-white/60 text-lg leading-relaxed mb-10 max-w-md">
              {t('home.heroDesc')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/collection" className="bg-[#00aaff] hover:bg-[#0088dd] text-white inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all" data-testid="explore-collection-btn">
                {t('home.exploreCollection')} <ArrowRight size={16} />
              </Link>
              <Link to="/technologie" className="border border-white/30 text-white hover:border-white/60 backdrop-blur-sm inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all">
                {t('home.discoverTech')}
              </Link>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-12 pt-8 border-t border-white/10">
              {[["12k+", t('home.clients')], ["4.9", t('home.rating')], ["#1", t('home.china')], ["+10", t('home.countries')]].map(([val, label]) => (
                <div key={label}>
                  <p className="text-2xl font-black text-[#00aaff]">{val}</p>
                  <p className="text-xs text-white/40 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative hidden md:flex justify-center">
            <div className="relative w-80 h-80 md:w-96 md:h-96">
              <div className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,170,255,0.2) 0%, transparent 70%)" }} />
              <Link to="/produit/newfarer-solaire?color=rose-dégradé" data-testid="hero-image-newfarer-link" className="block w-full h-full cursor-pointer">
                <img src="/api/uploads/homepage/bestseller-2026.jpg" alt="Joey Cyan smart glasses — NEWFARER" className="w-full h-full object-cover rounded-3xl shadow-2xl opacity-90 transition-all duration-300 hover:opacity-100 hover:scale-[1.02]" />
              </Link>
              <div className="absolute -bottom-4 -left-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2">
                  <Star size={14} className="text-[#00aaff]" fill="#00aaff" />
                  <span className="text-sm font-semibold text-white">{t('home.bestseller')}</span>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-[#00aaff] rounded-2xl p-3 shadow-lg">
                <Zap size={20} className="text-white" fill="white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 px-6 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-10">
            <div>
              <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">{t('home.collectionLabel')}</p>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">{t('home.collectionTitle')}</h2>
            </div>
            <Link to="/collection" className="hidden md:flex items-center gap-1 text-sm text-gray-400 hover:text-[#00aaff] transition-colors">
              {t('home.seeAll')} <ChevronRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : products.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(p => (
                <Link key={p.id} to={`/produit/${p.slug || p.id}`} data-testid={`product-card-${p.id}`}>
                  <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#00aaff]/40 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                    <div className="relative h-48 overflow-hidden bg-[#f0f4f8]">
                      {p.images && p.images.length > 0 ? (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300"><Eye size={40} /></div>
                      )}
                      {p.is_new && <span className="absolute top-3 left-3 bg-[#00aaff] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span>}
                      {p.is_bestseller && <span className="absolute top-3 right-3 bg-gray-900/70 backdrop-blur text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">BESTSELLER</span>}
                    </div>
                    <div className="p-4">
                      <p className="font-semibold text-sm text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{tc(p.subtitle)}</p>
                      {p.blue_light_filter && (
                        <div className="flex items-center gap-1 mt-2 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1">
                          <ShieldCheck size={11} className="text-violet-500 shrink-0" />
                          <span className="text-[10px] text-violet-600 font-medium">{t('home.blueLight')}</span>
                        </div>
                      )}
                      <ProductBadge badge={p.custom_badge} size="sm" />
                      <div className="flex justify-between items-center mt-3">
                        <span className="font-black text-[#00aaff]">{p.price}€</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full capitalize">{tc(p.category)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <Eye size={40} className="mx-auto mb-4 opacity-30" />
              <p>{t('home.noProducts')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 bg-[#f5f7fa]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">{t('home.techLabel')}</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">{t('home.techTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#00aaff]/40 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-[#e0f4ff] rounded-xl flex items-center justify-center mb-4">
                  <f.icon size={24} className="text-[#00aaff]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prise en charge 100% */}
      <section className="relative py-24 md:py-32 px-6 bg-white overflow-hidden" data-testid="reimbursement-section">
        <div aria-hidden className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-[#00aaff]/[0.04] blur-3xl" />

        <div className="relative max-w-5xl mx-auto">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#00aaff]" />
              <span className="inline-flex items-center gap-2 bg-[#00aaff] text-white px-5 py-2 rounded-full text-sm font-bold uppercase tracking-[0.22em] shadow-md shadow-[#00aaff]/30">
                Prise en charge 100%
              </span>
              <span className="h-px w-10 bg-[#00aaff]" />
            </div>

            <h2 className="text-[32px] md:text-[52px] font-black text-gray-900 leading-[1.05] tracking-tight mb-6">
              Vos verres Joey Cyan<br className="hidden sm:block" /> remboursés{' '}
              <span className="relative inline-block">
                <span className="relative z-10">intégralement</span>
                <span aria-hidden className="absolute left-0 right-0 bottom-1 h-[10px] bg-[#00aaff]/40 -z-0" />
              </span>
            </h2>

            <p className="text-gray-500 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Grâce à notre réseau de <span className="text-gray-900 font-semibold">partenaires opticiens agréés</span>, faites adapter vos montures Joey Cyan à votre vue. Tous nos verres avec anti-reflets sont pris en charge intégralement — mutuelle, CMU-C (C2S).{' '}
              <span className="text-gray-900 font-semibold">Aucun reste à charge.</span>
            </p>
          </div>

          {/* Three pillars — épuré */}
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-300 border-y border-gray-300">
            {[
              {
                Icon: ShieldCheck,
                title: 'Mutuelle acceptée',
                desc: 'Toutes les mutuelles complémentaires',
              },
              {
                Icon: Heart,
                title: 'CMU-C / C2S',
                desc: 'Prise en charge 100 % garantie',
              },
              {
                Icon: BadgeCheck,
                title: 'Anti-reflets offert',
                desc: 'Zéro reste à charge sur tous nos verres',
              },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="group px-6 md:px-10 py-10 md:py-12 flex flex-col items-center text-center transition-all duration-300"
                data-testid={`reimbursement-pillar-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="relative mb-5">
                  <div className="absolute inset-0 rounded-full bg-[#00aaff]/10 scale-0 group-hover:scale-100 transition-transform duration-500" />
                  <div className="relative w-14 h-14 rounded-full bg-white border border-[#00aaff]/20 flex items-center justify-center group-hover:border-[#00aaff] transition-colors duration-300">
                    <Icon size={22} className="text-[#00aaff]" strokeWidth={1.75} />
                  </div>
                </div>
                <h3 className="font-bold text-gray-900 text-lg tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-[220px]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-white border-t border-gray-100" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">{t('home.testimonialsLabel')}</p>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900">{t('home.testimonialsTitle')}</h2>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Stars rating={5} />
              <span className="text-sm text-gray-500 font-medium">{t('home.testimonialsRating')}</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((review) => {
              const reviewPhotos = Array.isArray(review.photos) ? review.photos.filter(Boolean) : [];
              return (
                <div key={review.id} className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-all duration-300">
                  <Stars rating={review.rating} />
                  <p className="text-sm text-gray-600 mt-3 leading-relaxed italic">"{review.comment}"</p>
                  {reviewPhotos.length > 0 && (
                    <div className="mt-4">
                      <ReviewPhotos photos={reviewPhotos} size="sm" maxThumbs={4} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-4">
                    <div className="w-8 h-8 rounded-full bg-[#e0f4ff] flex items-center justify-center text-[#00aaff] font-bold text-sm">
                      {review.author_name?.[0] || "?"}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{review.author_name}</p>
                      {review.product_name && <p className="text-xs text-gray-400">{review.product_name}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-6 bg-[#f5f7fa]">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-[#00aaff] to-[#0077cc] p-12 md:p-16">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 translate-x-16 -translate-y-16" />
            <div className="relative z-10 max-w-lg">
              <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-3">{t('home.ctaLabel')}</p>
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">{t('home.ctaTitle')}</h2>
              <p className="text-white/70 mb-8">{t('home.ctaDesc')}</p>
              <Link to="/collection" className="inline-flex items-center gap-2 bg-white text-[#00aaff] px-6 py-3 rounded-full text-sm font-bold hover:bg-gray-50 transition-colors">
                {t('home.ctaButton')} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
